import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useToast } from "../../../context/ToastContext.jsx";
import { FaTimes } from "react-icons/fa";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";

const MAX_CONVERSATION_IDS = 5000;
const MAX_CUSTOMER_IDS = 1000;

async function parseCallDownloadUpload(file, filterMode) {
  const requiredHeader = filterMode === "conversation_ids" ? "ID" : "CUSTOMER ID";
  const maxCount = filterMode === "conversation_ids" ? MAX_CONVERSATION_IDS : MAX_CUSTOMER_IDS;
  const name = (file.name || "").toLowerCase();
  let raw = [];
  if (name.endsWith(".txt")) {
    const text = await file.text();
    raw = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  } else {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!rows.length) throw new Error("Empty file.");
    const header = rows[0].map((c) => String(c).trim());
    const idx = header.findIndex((h) => h.toLowerCase() === requiredHeader.toLowerCase());
    if (idx === -1) {
      throw new Error(`The first row must include a "${requiredHeader}" column header.`);
    }
    for (let r = 1; r < rows.length; r++) {
      const v = String(rows[r][idx] ?? "").trim();
      if (v) raw.push(v);
    }
  }
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  if (!ids.length) throw new Error("No IDs found in file.");
  if (ids.length > maxCount) {
    throw new Error(`Maximum ${maxCount} IDs allowed (found ${ids.length}).`);
  }
  return ids;
}

const INITIAL = {
  metadata: true,
  transcript: false,
  futureScheduling: false,
  crmUpdatePayload: false,
  requestData: false,
  toolCalls: false,
  subparameters: false,
  conversationSummary: false,
  businessInsight: false,
  explanation: false
};

const LABELS = {
  metadata: "Metadata",
  transcript: "Transcript",
  futureScheduling: "Future scheduling",
  crmUpdatePayload: "CRM update payload",
  requestData: "Request data",
  toolCalls: "Tool calls",
  subparameters: "Subparameters",
  conversationSummary: "Conversation summary",
  businessInsight: "Business insight",
  explanation: "Explanation"
};

const OPTIONAL_KEYS = [
  "futureScheduling",
  "crmUpdatePayload",
  "requestData",
  "toolCalls",
  "conversationSummary",
  "businessInsight"
];

/**
 * Voice agents — download conversations modal: filter mode + field selection (not shared with main dashboard list).
 */
export default function VoiceCallDownloadParametersModal({
  isOpen,
  onClose,
  onApply,
  quickFilterApproxCount,
  filterMode,
  setFilterMode,
  setConversationIds,
  setCustomerIds
}) {
  const { showToast } = useToast();
  const [selected, setSelected] = useState(INITIAL);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [parsedIds, setParsedIds] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(INITIAL);
    setShowAdvancedOptions(false);
    setUploadFile(null);
    setParsedIds([]);
  }, [isOpen]);

  const areAllOptionalsSelected = useMemo(
    () => OPTIONAL_KEYS.every((k) => selected[k] === true),
    [selected]
  );

  if (!isOpen) return null;

  const toggle = (key) => {
    if (key === "metadata") return;
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setAllOptionals = (on) => {
    setSelected((prev) => {
      const n = { ...prev };
      OPTIONAL_KEYS.forEach((k) => {
        n[k] = on;
      });
      n.metadata = true;
      return n;
    });
  };

  const buildParametersList = () => {
    const list = [LABELS.metadata];
    if (selected.transcript) list.push(LABELS.transcript);
    if (selected.futureScheduling) list.push(LABELS.futureScheduling);
    if (selected.crmUpdatePayload) list.push(LABELS.crmUpdatePayload);
    if (selected.requestData) list.push(LABELS.requestData);
    if (selected.toolCalls) list.push(LABELS.toolCalls);
    if (selected.subparameters) list.push(LABELS.subparameters);
    if (selected.conversationSummary) list.push(LABELS.conversationSummary);
    if (selected.businessInsight) list.push(LABELS.businessInsight);
    if (selected.explanation) list.push(LABELS.explanation);
    return list;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setUploadFile(file || null);
    setParsedIds([]);
    if (!file) return;
    try {
      const ids = await parseCallDownloadUpload(file, filterMode);
      setParsedIds(ids);
      if (filterMode === "conversation_ids") {
        setConversationIds(ids);
        setCustomerIds([]);
      } else if (filterMode === "customer_ids") {
        setCustomerIds(ids);
        setConversationIds([]);
      }
      showToast(`Loaded ${ids.length} ID(s) from file.`, "success");
    } catch (err) {
      showToast(err?.message || "Could not read file.", "error");
    }
    e.target.value = "";
  };

  const handleApply = async () => {
    const parametersJson = JSON.stringify(buildParametersList());
    if (filterMode === "quick") {
      await onApply({
        parametersJson,
        filterMode: "quick",
        previewTotal: typeof quickFilterApproxCount === "number" ? quickFilterApproxCount : null
      });
      return;
    }
    if (!parsedIds.length) {
      showToast("Upload a file with IDs first.", "error");
      return;
    }
    await onApply({
      parametersJson,
      filterMode,
      parsedIds
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      <div
        className="relative bg-white rounded-lg shadow-lg w-full pt-6 pb-8 px-8 max-h-[95vh] overflow-y-auto"
        style={{ maxWidth: "60rem" }}
      >
        <div className="mb-4 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Download conversations</h3>
            <p className="text-sm text-gray-500">Choose how rows are selected, then pick which fields to include.</p>
          </div>
          <div>
            <button type="button" className="text-sm text-gray-500" onClick={onClose}>
              <FaTimes style={{ color: "slategray", cursor: "pointer" }} />
            </button>
          </div>
        </div>
        <div className="h-[70vh] overflow-y-auto">
          <div className="mb-5 rounded-md border border-gray-200 p-3 bg-gray-50">
            <div className="text-sm font-medium text-gray-800 mb-2">Filtering type</div>
            <div className=" text-sm text-gray-700 flex justify-between">
              <label className="flex items-start gap-2 cursor-pointer w-full">
                <input
                  type="radio"
                  name="voice-call-dl-filter"
                  className="mt-1"
                  checked={filterMode === "quick"}
                  onChange={() => {
                    setFilterMode("quick");
                    setUploadFile(null);
                    setParsedIds([]);
                    setConversationIds([]);
                    setCustomerIds([]);
                  }}
                />
                <span>
                  <span className="font-medium text-gray-900">Quick filters</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Toolbar and advanced filters will be applied for selection.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer w-full">
                <input
                  type="radio"
                  name="voice-call-dl-filter"
                  className="mt-1"
                  checked={filterMode === "conversation_ids"}
                  onChange={() => {
                    setFilterMode("conversation_ids");
                    setUploadFile(null);
                    setParsedIds([]);
                    setCustomerIds([]);
                  }}
                />
                <span>
                  <span className="font-medium text-gray-900">Upload conversation IDs</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Upload a CSV with one conversation ID per row. Filters are ignored.
                    <br />
                    Column header: ID, Limit: 5,000 conversations
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer w-full">
                <input
                  type="radio"
                  name="voice-call-dl-filter"
                  className="mt-1"
                  checked={filterMode === "customer_ids"}
                  onChange={() => {
                    setFilterMode("customer_ids");
                    setUploadFile(null);
                    setParsedIds([]);
                    setConversationIds([]);
                  }}
                />
                <span>
                  <span className="font-medium text-gray-900">Upload customer IDs</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Upload a CSV with one customer ID per row. Filters are ignored.
                    <br />
                    Column header: CUSTOMER ID, Limit: 1,000 customers.
                  </span>
                </span>
              </label>
            </div>

            {filterMode !== "quick" && (
              <div className="mt-3 space-y-2">
                <input
                  type="file"
                  accept=".csv,.txt,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                  className="block w-full text-xs text-gray-600"
                />
                {uploadFile && <p className="text-xs text-gray-600">File: {uploadFile.name}</p>}
                {parsedIds.length > 0 && <p className="text-xs text-gray-600">{parsedIds.length} ID(s) loaded.</p>}
              </div>
            )}
          </div>

          <div className="mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Choose data to include</h4>
            <p className="text-xs text-gray-500">Metadata is always included.</p>
          </div>
          <div className="border rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">Fields</div>
              <button
                type="button"
                className="text-xs text-indigo-600"
                onClick={() => setAllOptionals(!areAllOptionalsSelected)}
              >
                {areAllOptionalsSelected ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked readOnly disabled />
                {LABELS.metadata}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.futureScheduling}
                  onChange={() => toggle("futureScheduling")}
                />
                {LABELS.futureScheduling}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.crmUpdatePayload}
                  onChange={() => toggle("crmUpdatePayload")}
                />
                {LABELS.crmUpdatePayload}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.requestData}
                  onChange={() => toggle("requestData")}
                />
                {LABELS.requestData}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.toolCalls}
                  onChange={() => toggle("toolCalls")}
                />
                {LABELS.toolCalls}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.conversationSummary}
                  onChange={() => toggle("conversationSummary")}
                />
                {LABELS.conversationSummary}
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={selected.businessInsight}
                  onChange={() => toggle("businessInsight")}
                />
                {LABELS.businessInsight}
              </label>
            </div>
          </div>
          <div className="h-[120px] overflow-y-auto mt-3">
            <div className="flex items-center justify-between w-[30%] mb-2">
              <div className="text-sm font-medium text-gray-700">Advanced options</div>
              <button type="button" className="text-xs" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
                {showAdvancedOptions ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>
            </div>
            {showAdvancedOptions && (
              <>
                <label className="flex items-center text-sm mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selected.transcript}
                    onChange={() => toggle("transcript")}
                  />
                  {LABELS.transcript}
                </label>
                <label className="flex items-center text-sm mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selected.subparameters}
                    onChange={() => toggle("subparameters")}
                  />
                  {LABELS.subparameters}
                </label>
                <label className="flex items-center text-sm mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selected.explanation}
                    onChange={() => toggle("explanation")}
                  />
                  {LABELS.explanation}
                </label>
              </>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded text-white"
            style={{ backgroundColor: "rgb(79, 70, 229)" }}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
